import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, TableCellsIcon, MagnifyingGlassIcon, ArrowLeftIcon } from "@heroicons/react/20/solid"
import { CameraIcon, DocumentTextIcon, FolderIcon } from "@heroicons/react/24/outline"
import EnkPageContainer from "../components/page-container"
import PhotoGallery from "../components/dataexplorer/photo-gallery"
import EnkLoader from "../components/common/loader"
import { useAuth } from "../auth/use-auth"
import { classNames } from "../utils/styling-utils"
import {
  EnkIWPServerDataQueries,
  TEnkDataQueryInfo,
  TEnkDataQueryParamInfo,
  TEnkDataQueryResult,
  TEnkDataQueryColumn,
} from "../api/api-iwp-server"

/** Lokaal type voor naam/waarde paar bij parameters */
type TEnkDataQueryParam = { name: string; value: string }

/** ParamInfo met gegarandeerd ingevulde paramNaam (na filter) */
type TRequiredParamInfo = TEnkDataQueryParamInfo & { paramNaam: string }

/** Parsed _Toon link met prefix en waarde */
interface ToonLink {
  type: "report" | "dataquery" | "documents" | "url" | "photos"
  value: string
  params?: TEnkDataQueryParam[]
  photoTable?: string
  photoKeyValue?: string
}

/** Parse een _Toon cel-waarde naar type + navigatie-waarde.
 *  Ondersteunt query string parameters: dataquery:2?Opdrachtbonnr=O12345&param2=waarde */
function parseToonValue(raw: string): ToonLink | null {
  if (!raw) return null
  if (raw.startsWith("report:")) {
    const rest = raw.substring(7)
    const qIdx = rest.indexOf("?")
    if (qIdx === -1) return { type: "report", value: rest }
    const name = rest.substring(0, qIdx)
    const qs = rest.substring(qIdx + 1)
    const params: TEnkDataQueryParam[] = qs.split("&").filter(Boolean).map((pair) => {
      const eqIdx = pair.indexOf("=")
      if (eqIdx === -1) return { name: pair, value: "" }
      return { name: pair.substring(0, eqIdx), value: decodeURIComponent(pair.substring(eqIdx + 1)) }
    })
    return { type: "report", value: name, params }
  }
  if (raw.startsWith("dataquery:")) {
    const rest = raw.substring(10)
    const qIdx = rest.indexOf("?")
    if (qIdx === -1) return { type: "dataquery", value: rest }
    const id = rest.substring(0, qIdx)
    const qs = rest.substring(qIdx + 1)
    const params: TEnkDataQueryParam[] = qs.split("&").filter(Boolean).map((pair) => {
      const eqIdx = pair.indexOf("=")
      if (eqIdx === -1) return { name: pair, value: "" }
      return { name: pair.substring(0, eqIdx), value: decodeURIComponent(pair.substring(eqIdx + 1)) }
    })
    return { type: "dataquery", value: id, params }
  }
  if (raw.startsWith("photos:")) {
    const rest = raw.substring(7)
    const commaIdx = rest.indexOf(",")
    if (commaIdx === -1) return null
    return { type: "photos", value: raw, photoTable: rest.substring(0, commaIdx), photoKeyValue: rest.substring(commaIdx + 1) }
  }
  if (raw.startsWith("documents:")) return { type: "documents", value: raw.substring(10) }
  if (raw.startsWith("/")) return { type: "url", value: raw }
  return null
}

/** Navigatie-stack entry voor dataquery: terugnavigatie */
interface QueryHistoryEntry {
  queryId: number
  queryName: string
}

type SortDirection = "asc" | "desc" | null

interface SortState {
  columnIndex: number
  direction: SortDirection
}

function compareValues(a: string, b: string, dataType: string): number {
  if (dataType === "number") {
    const numA = parseFloat(a) || 0
    const numB = parseFloat(b) || 0
    return numA - numB
  }
  if (dataType === "date") {
    const dateA = new Date(a).getTime() || 0
    const dateB = new Date(b).getTime() || 0
    return dateA - dateB
  }
  return a.localeCompare(b, "nl")
}

function matchesFilter(value: string, filter: string, dataType: string): boolean {
  if (!filter) return true
  if (dataType === "number") {
    // Ondersteun >, <, >=, <=, = prefixes
    const match = filter.match(/^([><=!]{1,2})\s*(.+)/)
    if (match) {
      const op = match[1]
      const num = parseFloat(match[2])
      const val = parseFloat(value) || 0
      if (isNaN(num)) return true
      switch (op) {
        case ">": return val > num
        case "<": return val < num
        case ">=": return val >= num
        case "<=": return val <= num
        case "=": return val === num
        case "!=": return val !== num
        default: return true
      }
    }
    return value.includes(filter)
  }
  return value.toLowerCase().includes(filter.toLowerCase())
}

export default function EnkDataExplorer() {
  const { getCurrentGroupId } = useAuth()
  const navigate = useNavigate()

  // Query lijst state
  const [queries, setQueries] = useState<TEnkDataQueryInfo[]>([])
  const [loadingQueries, setLoadingQueries] = useState(true)
  const [selectedQueryId, setSelectedQueryId] = useState<number | null>(null)

  // Data state
  const [result, setResult] = useState<TEnkDataQueryResult | null>(null)
  const [loadingData, setLoadingData] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filter en sorteer state
  const [filters, setFilters] = useState<string[]>([])
  const [sort, setSort] = useState<SortState>({ columnIndex: -1, direction: null })

  // Paginering state
  const pageSize = 500
  const [offset, setOffset] = useState(0)

  // Photo gallery state
  const [photoGallery, setPhotoGallery] = useState<{ open: boolean; table: string; keyValue: string }>({ open: false, table: "", keyValue: "" })

  // Navigatie-stack voor dataquery: terugnavigatie
  const [queryHistory, setQueryHistory] = useState<QueryHistoryEntry[]>([])

  // Splits kolommen in data-kolommen en _Toon-kolommen
  const { dataColumns, toonColumns } = useMemo(() => {
    if (!result) return { dataColumns: [] as (TEnkDataQueryColumn & { origIndex: number })[], toonColumns: [] as (TEnkDataQueryColumn & { origIndex: number })[] }
    const data: (TEnkDataQueryColumn & { origIndex: number })[] = []
    const toon: (TEnkDataQueryColumn & { origIndex: number })[] = []
    result.columns.forEach((col, i) => {
      if ((col.name ?? "").toLowerCase().startsWith("_toon")) {
        toon.push({ ...col, origIndex: i })
      } else {
        data.push({ ...col, origIndex: i })
      }
    })
    return { dataColumns: data, toonColumns: toon }
  }, [result])

  // Haal query lijst op
  useEffect(() => {
    async function fetchQueries() {
      try {
        const groupId = getCurrentGroupId()
        const list = await EnkIWPServerDataQueries.getList(groupId)
        setQueries(list)
        setError(null)
      } catch (err) {
        console.error("Failed to fetch data queries:", err)
        setError("Fout bij het ophalen van de beschikbare queries.")
      }
      setLoadingQueries(false)
    }
    void fetchQueries()
  }, [getCurrentGroupId])

  // Actieve params (voor de huidige query, vanuit dataquery: navigatie)
  const [activeParams, setActiveParams] = useState<TEnkDataQueryParam[]>([])

  // Parameter formulier state
  const [paramDefs, setParamDefs] = useState<TRequiredParamInfo[]>([])
  const [paramValues, setParamValues] = useState<Record<string, string>>({})
  const [showParamForm, setShowParamForm] = useState(false)

  // Haal data op
  const fetchData = async (queryId: number, newOffset: number, params?: TEnkDataQueryParam[]) => {
    setLoadingData(true)
    setError(null)

    try {
      const requestParams = params ?? activeParams
      const data = await EnkIWPServerDataQueries.getData({
        id: queryId, limit: pageSize, offset: newOffset,
        paramNames: requestParams.length > 0 ? requestParams.map((p) => p.name) : [],
        paramValues: requestParams.length > 0 ? requestParams.map((p) => p.value) : [],
      })
      setResult(data)
      setOffset(newOffset)
      if (newOffset === 0) {
        const dataColCount = data.columns.filter((c) => !(c.name ?? "").toLowerCase().startsWith("_toon")).length
        setFilters(new Array(dataColCount).fill(""))
      }
    } catch (err) {
      console.error("Failed to fetch query data:", err)
      setError("Fout bij het uitvoeren van de query.")
    }
    setLoadingData(false)
  }

  // Zorg dat datum in yyyy-mm-dd formaat staat (NexusDB verwacht dit)
  const formatDateValue = (value: string): string => {
    if (!value) return value
    // dd-mm-yyyy of dd/mm/yyyy → yyyy-mm-dd
    const match = value.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/)
    if (match) return `${match[3]}-${match[2]}-${match[1]}`
    return value
  }

  // Bouw params array vanuit paramValues state
  const buildParamsFromForm = (): TEnkDataQueryParam[] => {
    return paramDefs.map((def) => {
      const raw = paramValues[def.paramNaam] ?? def.defaultWaarde ?? ""
      return {
        name: def.paramNaam,
        value: def.paramType === "date" ? formatDateValue(raw) : raw,
      }
    })
  }

  // Check of verplichte params ingevuld zijn
  const hasRequiredParams = (): boolean => {
    return paramDefs.filter((d) => d.verplicht).every((d) => {
      const val = paramValues[d.paramNaam] ?? d.defaultWaarde ?? ""
      return val.trim() !== ""
    })
  }

  // Uitvoeren vanuit parameterformulier
  const handleParamSubmit = async () => {
    if (selectedQueryId == null) return
    const params = buildParamsFromForm()
    setActiveParams(params)
    await fetchData(selectedQueryId, 0, params)
  }

  // Haal data op bij selectie
  const handleQuerySelect = async (queryId: number) => {
    setSelectedQueryId(queryId)
    setResult(null)
    setFilters([])
    setSort({ columnIndex: -1, direction: null })
    setOffset(0)
    setQueryHistory([])
    setActiveParams([])

    // Check of query parameters heeft
    const query = queries.find((q) => q.id === queryId)
    const defs = (query?.params?.filter((p): p is TRequiredParamInfo => !!p.paramNaam)) ?? []
    if (defs.length > 0) {
      // Sorteer op volgorde
      defs.sort((a, b) => a.volgorde - b.volgorde)
      setParamDefs(defs)
      // Vul defaults in
      const defaults: Record<string, string> = {}
      for (const d of defs) {
        if (d.defaultWaarde) defaults[d.paramNaam] = d.defaultWaarde
      }
      setParamValues(defaults)
      setShowParamForm(true)
      return // Wacht op gebruiker
    }

    setParamDefs([])
    setParamValues({})
    setShowParamForm(false)
    await fetchData(queryId, 0, [])
  }

  // Navigeer naar een andere dataquery (vanuit _Toon kolom)
  const handleDataQueryNavigate = async (targetQueryId: number, params?: TEnkDataQueryParam[]) => {
    if (selectedQueryId == null) return
    const currentName = queries.find((q) => q.id === selectedQueryId)?.naam ?? `Query ${selectedQueryId}`
    setQueryHistory((prev) => [...prev, { queryId: selectedQueryId, queryName: currentName }])
    setSelectedQueryId(targetQueryId)
    setResult(null)
    setFilters([])
    setSort({ columnIndex: -1, direction: null })
    setOffset(0)
    const newParams = params ?? []
    setActiveParams(newParams)

    // Zoek paramDefs voor de doelquery (kan een verborgen query zijn die niet in queries[] staat)
    const targetQuery = queries.find((q) => q.id === targetQueryId)
    const defs = (targetQuery?.params?.filter((p): p is TRequiredParamInfo => !!p.paramNaam)) ?? []
    if (defs.length > 0) {
      defs.sort((a, b) => a.volgorde - b.volgorde)
      setParamDefs(defs)
      // Pre-fill vanuit navigatie params + defaults
      const vals: Record<string, string> = {}
      for (const d of defs) {
        if (d.defaultWaarde) vals[d.paramNaam] = d.defaultWaarde
      }
      for (const p of newParams) {
        vals[p.name] = p.value
      }
      setParamValues(vals)
      setShowParamForm(true)
      // Als alle params via navigatie meekomen, direct uitvoeren
      const allFilled = defs.filter((d) => d.verplicht).every((d) => (vals[d.paramNaam] ?? "").trim() !== "")
      if (newParams.length > 0 && allFilled) {
        const finalParams = defs.map((d) => ({ name: d.paramNaam, value: vals[d.paramNaam] ?? "" }))
        setActiveParams(finalParams)
        await fetchData(targetQueryId, 0, finalParams)
      }
    } else {
      setParamDefs([])
      setParamValues({})
      setShowParamForm(false)
      await fetchData(targetQueryId, 0, newParams)
    }
  }

  // Terug naar vorige dataquery
  const handleQueryBack = async () => {
    const prev = queryHistory[queryHistory.length - 1]
    if (!prev) return
    setQueryHistory((h) => h.slice(0, -1))
    setSelectedQueryId(prev.queryId)
    setResult(null)
    setFilters([])
    setSort({ columnIndex: -1, direction: null })
    setOffset(0)
    setActiveParams([])
    setParamDefs([])
    setParamValues({})
    setShowParamForm(false)
    await fetchData(prev.queryId, 0, [])
  }

  // Klik op een _Toon cel
  const handleToonClick = (raw: string) => {
    const link = parseToonValue(raw)
    if (!link) return
    switch (link.type) {
      case "report": {
        const qs = link.params?.map((p) => `${encodeURIComponent(p.name)}=${encodeURIComponent(p.value)}`).join("&")
        const reportName = link.value.replace(/\//g, "\\")
        navigate(`/report/${encodeURIComponent(reportName)}${qs ? "?" + qs : ""}`)
        break
      }
      case "dataquery": {
        const id = parseInt(link.value)
        if (!isNaN(id)) void handleDataQueryNavigate(id, link.params)
        break
      }
      case "photos":
        if (link.photoTable && link.photoKeyValue) {
          setPhotoGallery({ open: true, table: link.photoTable, keyValue: link.photoKeyValue })
        }
        break
      case "url":
        navigate(link.value)
        break
    }
  }

  // Paginering
  const handlePrevPage = () => {
    if (selectedQueryId == null || offset <= 0) return
    void fetchData(selectedQueryId, Math.max(0, offset - pageSize))
  }

  const handleNextPage = () => {
    if (selectedQueryId == null || !result?.limited) return
    void fetchData(selectedQueryId, offset + pageSize)
  }

  // Sorteer kolomklik
  const handleSortClick = (colIndex: number) => {
    setSort((prev) => {
      if (prev.columnIndex === colIndex) {
        // Wissel: asc -> desc -> geen
        if (prev.direction === "asc") return { columnIndex: colIndex, direction: "desc" }
        if (prev.direction === "desc") return { columnIndex: -1, direction: null }
      }
      return { columnIndex: colIndex, direction: "asc" }
    })
  }

  // Filter wijzigen
  const handleFilterChange = (colIndex: number, value: string) => {
    setFilters((prev) => {
      const next = [...prev]
      next[colIndex] = value
      return next
    })
  }

  // Gefilterde en gesorteerde rijen (filters/sort werken op dataColumns indices)
  const processedRows = useMemo(() => {
    if (!result) return []

    let rows = result.rows

    // Filter (alleen op datakolommen)
    if (filters.some((f) => f)) {
      rows = rows.filter((row) =>
        dataColumns.every((col, i) => matchesFilter(row.values[col.origIndex] ?? "", filters[i] || "", col.dataType || "string"))
      )
    }

    // Sorteer (sort.columnIndex verwijst naar positie in dataColumns)
    if (sort.columnIndex >= 0 && sort.direction) {
      const origIdx = dataColumns[sort.columnIndex]?.origIndex
      if (origIdx != null) {
        const dataType = dataColumns[sort.columnIndex]?.dataType || "string"
        const dir = sort.direction === "asc" ? 1 : -1
        rows = [...rows].sort((a, b) => dir * compareValues(a.values[origIdx] ?? "", b.values[origIdx] ?? "", dataType))
      }
    }

    return rows
  }, [result, filters, sort, dataColumns])

  return (
    <EnkPageContainer>
      <div className="flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 5rem)' }}>
        {/* Loading overlay */}
        {(loadingQueries || loadingData) && <EnkLoader />}

        {/* Error melding */}
        {error && (
          <div className="shrink-0 text-red-500 text-sm font-semibold py-2 px-4 bg-red-100 rounded-md mb-4">{error}</div>
        )}

        {/* Terugknop */}
        {result && (
          <div className="shrink-0 mb-2">
            {queryHistory.length > 0 ? (
              <button
                onClick={() => void handleQueryBack()}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Terug naar {queryHistory[queryHistory.length - 1].queryName}
              </button>
            ) : (
              <button
                onClick={() => { setResult(null); setSelectedQueryId(null); setShowParamForm(false); setParamDefs([]); setParamValues({}) }}
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Terug naar overzicht
              </button>
            )}
          </div>
        )}

        {/* Query selectie als kaarten (zichtbaar wanneer geen data geladen) */}
        {!result && !loadingData && !loadingQueries && queries.length > 0 && (
          <div className="shrink-0 overflow-auto">
            <ul role="list" className="divide-y divide-gray-100">
              {queries.map((q) => (
                <li key={q.id} className="flex items-center justify-between gap-x-6 py-5">
                  <div className="min-w-0">
                    <div className="flex items-start gap-x-3">
                      <p className="text-sm/6 font-semibold text-gray-900">{q.naam}</p>
                    </div>
                    {q.beschrijving && (
                      <div className="mt-1 flex items-center gap-x-2 text-xs/5 text-gray-500">
                        <p className="whitespace-nowrap">{q.beschrijving}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-none items-center gap-x-4">
                    <button
                      onClick={() => void handleQuerySelect(q.id)}
                      className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Openen<span className="sr-only">, {q.naam}</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Geen queries beschikbaar */}
        {!loadingQueries && queries.length === 0 && !error && (
          <div className="shrink-0 flex flex-col items-center justify-center py-12 text-gray-400">
            <TableCellsIcon className="h-12 w-12 mb-3" />
            <p className="text-sm font-medium text-gray-500">Geen dataverkenners beschikbaar</p>
            <p className="text-xs text-gray-400 mt-1">Er zijn geen queries geconfigureerd voor deze groep.</p>
          </div>
        )}

        {/* Parameterformulier */}
        {showParamForm && paramDefs.length > 0 && (
          <div className="shrink-0 mb-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex flex-wrap items-end gap-4">
              {paramDefs.map((def) => (
                <div key={def.paramNaam} className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600">
                    {def.displayLabel || def.paramNaam}
                    {def.verplicht && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <input
                    type={def.paramType === "date" ? "date" : def.paramType === "number" ? "number" : "text"}
                    className="rounded border-gray-300 text-sm py-1.5 px-2.5 focus:border-primary focus:ring-primary min-w-[160px]"
                    value={paramValues[def.paramNaam] ?? ""}
                    onChange={(e) => setParamValues((prev) => ({ ...prev, [def.paramNaam]: e.target.value }))}
                    placeholder={def.displayLabel || def.paramNaam}
                  />
                </div>
              ))}
              <button
                onClick={() => void handleParamSubmit()}
                disabled={!hasRequiredParams()}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Uitvoeren
              </button>
            </div>
          </div>
        )}

        {/* Resultaat info en paginering */}
        {result && (
          <div className="shrink-0 flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">
              {offset + 1}–{offset + result.rows.length} van {result.totalCount} rijen
              {filters.some((f) => f) && processedRows.length !== result.rows.length && (
                <span className="ml-1">({processedRows.length} na filter)</span>
              )}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={handlePrevPage}
                disabled={offset <= 0}
                className="inline-flex items-center rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                Vorige
              </button>
              <button
                onClick={handleNextPage}
                disabled={!result.limited}
                className="inline-flex items-center rounded px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Volgende
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Scrollbare tabel container - vult resterende ruimte */}
        {result && (
          <div className="flex-1 min-h-0 overflow-auto border border-gray-200 rounded-lg">
            <table className="min-w-full border-separate border-spacing-0">
              <thead>
                {/* Kolomkoppen */}
                <tr>
                  {dataColumns.map((col, colIdx) => (
                    <th
                      key={col.origIndex}
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-3.5 px-3 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter cursor-pointer select-none hover:bg-gray-100/75"
                      onClick={() => handleSortClick(colIdx)}
                    >
                      <span className="flex items-center gap-1">
                        {col.name}
                        {sort.columnIndex === colIdx && sort.direction === "asc" && (
                          <ChevronUpIcon className="h-4 w-4 text-primary" />
                        )}
                        {sort.columnIndex === colIdx && sort.direction === "desc" && (
                          <ChevronDownIcon className="h-4 w-4 text-primary" />
                        )}
                      </span>
                    </th>
                  ))}
                  {/* _Toon kolomkoppen - sticky rechts */}
                  {toonColumns.map((col, toonIdx) => (
                    <th
                      key={col.origIndex}
                      scope="col"
                      className="sticky top-0 z-20 border-b border-gray-300 border-l bg-white py-3.5 px-2 text-center text-sm font-semibold text-gray-900"
                      style={{ right: (toonColumns.length - 1 - toonIdx) * 40 }}
                    >
                      <span className="sr-only">{col.name}</span>
                    </th>
                  ))}
                </tr>
                {/* Filter rij */}
                <tr>
                  {dataColumns.map((col, colIdx) => (
                    <th key={col.origIndex} className="sticky top-[44px] z-10 border-b border-gray-200 bg-gray-50/90 px-3 py-1 backdrop-blur backdrop-filter">
                      <input
                        type="text"
                        className="block w-full rounded border-gray-300 text-sm py-1 px-2 focus:border-primary focus:ring-primary"
                        placeholder="Filter..."
                        value={filters[colIdx] || ""}
                        onChange={(e) => handleFilterChange(colIdx, e.target.value)}
                      />
                    </th>
                  ))}
                  {/* Lege filter-cellen voor _Toon kolommen */}
                  {toonColumns.map((col, toonIdx) => (
                    <th
                      key={col.origIndex}
                      className="sticky top-[44px] z-20 border-b border-gray-200 border-l bg-gray-50 px-2 py-1"
                      style={{ right: (toonColumns.length - 1 - toonIdx) * 40 }}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {processedRows.length === 0 && (
                  <tr>
                    <td colSpan={dataColumns.length + toonColumns.length} className="text-center py-12">
                      <MagnifyingGlassIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Geen gegevens gevonden.</p>
                      {filters.some((f) => f) && (
                        <p className="text-xs text-gray-400 mt-1">Probeer de filters aan te passen.</p>
                      )}
                    </td>
                  </tr>
                )}
                {processedRows.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-gray-50">
                    {/* Datakolommen */}
                    {dataColumns.map((col) => (
                      <td
                        key={col.origIndex}
                        className={classNames(
                          rowIdx !== processedRows.length - 1 ? "border-b border-gray-200" : "",
                          col.dataType === "number" ? "text-right" : "",
                          "whitespace-nowrap py-3 px-3 text-sm text-gray-900 max-w-xs truncate"
                        )}
                        title={row.values[col.origIndex] ?? ""}
                      >
                        {row.values[col.origIndex] ?? ""}
                      </td>
                    ))}
                    {/* _Toon kolommen - sticky rechts met iconen */}
                    {toonColumns.map((col, toonIdx) => {
                      const raw = row.values[col.origIndex] ?? ""
                      const link = parseToonValue(raw)
                      return (
                        <td
                          key={col.origIndex}
                          className={classNames(
                            rowIdx !== processedRows.length - 1 ? "border-b border-gray-200" : "",
                            "sticky z-10 border-l bg-white whitespace-nowrap py-3 px-2 text-center"
                          )}
                          style={{ right: (toonColumns.length - 1 - toonIdx) * 40 }}
                        >
                          {link && (
                            <button
                              onClick={() => handleToonClick(raw)}
                              className="inline-flex items-center justify-center rounded p-1 text-gray-500 hover:text-primary hover:bg-gray-100"
                              title={raw}
                            >
                              {link.type === "report" && <DocumentTextIcon className="h-5 w-5" />}
                              {link.type === "dataquery" && <TableCellsIcon className="h-5 w-5" />}
                              {link.type === "documents" && <FolderIcon className="h-5 w-5" />}
                              {link.type === "photos" && <CameraIcon className="h-5 w-5" />}
                              {link.type === "url" && <DocumentTextIcon className="h-5 w-5" />}
                            </button>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <PhotoGallery
        open={photoGallery.open}
        onClose={() => setPhotoGallery({ open: false, table: "", keyValue: "" })}
        table={photoGallery.table}
        keyValue={photoGallery.keyValue}
      />
    </EnkPageContainer>
  )
}
